import React, { useCallback, useEffect, useMemo, useState } from "react"

import {
  useAnnotationsWithMetricOutputsTransport,
  useHumanReviewProjectsTransport,
  useReviewMetricTransportInvalidation,
} from "@/human-feedback/transport"
import {
  METRIC_VIEWER_EVENTS,
  metricViewerEventBus,
  useEventBus,
} from "@/metrics/registry"
import { useSimulationOutputTransportInvalidation } from "@/runs"
import { useScopedStorageKey } from "@/workspaces/scope"
import { Button, Tabs, TabsList, TabsTrigger, Tooltip } from "@coval/ui"
import { Play, Square, X } from "lucide-react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { logger } from "@/lib/logger"
import { useNavigationShortcuts } from "@/hooks/useNavigationShortcuts"
import type { HumanReviewProject } from "@/types/api/HumanReviewProject"

import { buildExperimentStorageKey } from "../../utils/experimentStorageKey"
import {
  ALL_PROJECTS_FILTER,
  canRevealHumanVsAiAgreement,
  DEFAULT_SIMULATION_FILTERS,
  extractSimulationIds,
  filterAndSortSimulations,
  getAnnotationProjectIds,
  getEffectiveAgreementFilter,
  groupAnnotationsBySimulation,
} from "../../utils/filterSimulations"
import {
  TEST_DRAWER_TAB_OPTIONS,
  useTestDrawer,
  useTestMetricRuntime,
  type ViewMode,
} from "../contexts"
import { useCanRunEvaluations } from "../hooks/useCanRunEvaluations"
import { MAX_TEST_CONVERSATIONS, resolveRunSelection } from "../runSelection"
import type { ExperimentTestResult as TestResult } from "../types"
import { useBackendExperimentRunner } from "../useBackendExperimentRunner"
import {
  AnnotationsFiltersPopover,
  type AnnotationsFilters,
} from "./AnnotationsFiltersPopover"
import { AudioMetricsSimulationDetailView } from "./AudioMetricsSimulationDetailView"
import { AudioMetricsSimulationReviewTable } from "./AudioMetricsSimulationReviewTable"
import { HumanReviewProjectFilter } from "./HumanReviewProjectFilter"
import { SimulationDetailView } from "./SimulationDetailView"
import { SimulationReviewTable } from "./SimulationReviewTable"

const log = logger("AnnotationsView")

export const AnnotationsView: React.FC<{
  /** Covers everything below the list or detail header, so it never hides header controls however many rows they wrap onto. */
  bodyOverlay?: React.ReactNode
  headerContent?: React.ReactNode
  headerTitle?: string
  scopeContent?: React.ReactNode
  showTabs?: boolean
  tabOptions?: typeof TEST_DRAWER_TAB_OPTIONS
  simulationOutputIds?: string[]
}> = ({
  bodyOverlay,
  headerContent,
  headerTitle = "Conversations",
  scopeContent,
  showTabs = true,
  tabOptions = TEST_DRAWER_TAB_OPTIONS,
  simulationOutputIds,
}) => {
  const {
    metric,
    toleranceSeconds: contextTolerance,
    setToleranceSeconds: setContextTolerance,
    simulationIds: scopedSimulationIds,
    annotationSimulationIds,
    criteriaOverride,
    experimentStorageScope,
    beforeRun,
    onRunningChange,
    criteriaViewMode,
  } = useTestMetricRuntime()
  const canRunEvaluations = useCanRunEvaluations()
  const { trackEvent } = useAnalytics()
  const invalidateReviewMetricTransport = useReviewMetricTransportInvalidation()
  const invalidateSimulationOutput = useSimulationOutputTransportInvalidation()
  const {
    viewMode,
    setViewMode,
    selectedSimulationId,
    handleRowClick,
    handleClose,
    clearFocusedSimulationIds,
    requestedFilters,
    consumeRequestedFilters,
    selectedRunIds,
  } = useTestDrawer()

  const usesRegions =
    metric?.supportsAudioSegmentReview === true &&
    metric.getAudioRegionReviewConfig()?.useRegions === true

  const [filters, setFilters] = useState<AnnotationsFilters>({
    ...DEFAULT_SIMULATION_FILTERS,
    project: ALL_PROJECTS_FILTER,
  })

  // Apply filters requested by stats card clicks.
  useEffect(() => {
    if (!requestedFilters) {
      return
    }
    const consumed = consumeRequestedFilters()
    if (!consumed) {
      return
    }
    setFilters((prev) => ({
      ...prev,
      ...(consumed.agreement != null ? { agreement: consumed.agreement } : {}),
      ...(consumed.includeUnreviewed != null
        ? { includeUnreviewed: consumed.includeUnreviewed }
        : {}),
      ...(consumed.includeWithoutMetricOutput != null
        ? { includeWithoutMetricOutput: consumed.includeWithoutMetricOutput }
        : {}),
    }))
  }, [requestedFilters, consumeRequestedFilters])

  // Fetch human review projects for filtering
  const { data: humanReviewProjects } = useHumanReviewProjectsTransport()

  const relevantProjects = useMemo(() => {
    if (!humanReviewProjects || !metric?.id) {
      return []
    }
    return humanReviewProjects.filter((p: HumanReviewProject) =>
      p.linked_metric_ids.includes(metric.id),
    )
  }, [humanReviewProjects, metric?.id])

  // Reset project filter when the selected project is no longer relevant
  useEffect(() => {
    if (
      filters.project !== ALL_PROJECTS_FILTER &&
      relevantProjects.length > 0 &&
      !relevantProjects.some((p) => p.ulid === filters.project)
    ) {
      setFilters((prev) => ({ ...prev, project: ALL_PROJECTS_FILTER }))
    }
    if (
      filters.project !== ALL_PROJECTS_FILTER &&
      relevantProjects.length === 0
    ) {
      setFilters((prev) => ({ ...prev, project: ALL_PROJECTS_FILTER }))
    }
  }, [filters.project, relevantProjects])

  // Include metadata fingerprint so editing the prompt invalidates cached results.
  const rawStorageKey = useMemo(
    () =>
      buildExperimentStorageKey(
        metric?.id,
        metric?.metric_metadata,
        experimentStorageScope,
      ),
    [metric?.id, metric?.metric_metadata, experimentStorageScope],
  )
  // Scoped by org + workspace for consistency with every other cache keyed
  // off `metric?.id` — the reads/writes below are already gated on a
  // resolved metric.id, so this mainly guards against that guard being
  // relaxed later.
  const storageKey = useScopedStorageKey(rawStorageKey)

  // Load persisted experiment results on mount
  const [experimentResults, setExperimentResults] = useState<
    Record<string, TestResult>
  >(() => {
    if (typeof window === "undefined" || !metric?.id || !storageKey) {
      return {}
    }
    try {
      const stored = sessionStorage.getItem(storageKey)
      if (stored) {
        return JSON.parse(stored) as Record<string, TestResult>
      }
    } catch (error) {
      log.error("Failed to load persisted experiment results", error)
    }
    return {}
  })

  // Save experiment results to sessionStorage whenever they change
  useEffect(() => {
    if (typeof window === "undefined" || !metric?.id || !storageKey) {
      return
    }
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(experimentResults))
      // Notify other components on the metric viewer page (e.g. the
      // confusion matrix) that test results changed so they can refresh.
      metricViewerEventBus.emit(
        METRIC_VIEWER_EVENTS.TEST_METRIC_RESULTS_UPDATED,
        { storageKey },
      )
    } catch (error) {
      log.warn("Failed to persist experiment results", { error })
    }
  }, [experimentResults, storageKey, metric?.id])

  const effectiveScopeIds =
    simulationOutputIds ?? annotationSimulationIds ?? scopedSimulationIds

  // Fetch annotations within the exact selected project when one is chosen.
  const annotationProjectIds = useMemo(
    () => getAnnotationProjectIds(filters.project),
    [filters.project],
  )
  const {
    data: annotationsWithMetricOutputs,
    isFetching: isFetchingAnnotations,
  } = useAnnotationsWithMetricOutputsTransport({
    metricId: metric?.id,
    projectIds: annotationProjectIds,
    simulationOutputIds: effectiveScopeIds,
    enabled: effectiveScopeIds === undefined || effectiveScopeIds.length > 0,
    includeMetricOutputDetails:
      metric?.metric_manager === "manager_composite_evaluation",
  })

  const canRevealAgreement = canRevealHumanVsAiAgreement(
    filters.project,
    relevantProjects,
    isFetchingAnnotations,
  )
  const effectiveFilters = useMemo<AnnotationsFilters>(
    () => ({
      ...filters,
      toleranceSeconds: contextTolerance,
      agreement: getEffectiveAgreementFilter(
        filters.agreement,
        filters.project,
        relevantProjects,
        isFetchingAnnotations,
      ),
    }),
    [filters, contextTolerance, relevantProjects, isFetchingAnnotations],
  )

  // Derived data - compute filtered and sorted simulation IDs to match table order
  const simulationIds = useMemo(() => {
    if (annotationProjectIds !== undefined && isFetchingAnnotations) {
      return []
    }
    const groupedData = groupAnnotationsBySimulation(
      annotationsWithMetricOutputs,
      metric?.id,
      metric?.output_type || "string",
      effectiveFilters.includeUnreviewed,
    )
    const metricOutputsForMetric =
      annotationsWithMetricOutputs?.metricOutputs[metric?.id || ""]
    const filteredAndSorted = filterAndSortSimulations(
      groupedData,
      effectiveFilters,
      metric,
      metricOutputsForMetric,
      humanReviewProjects,
      criteriaViewMode,
      annotationProjectIds !== undefined,
      isFetchingAnnotations,
    )
    const ids = extractSimulationIds(filteredAndSorted)
    if (!effectiveScopeIds) {
      return ids
    }
    const scope = new Set(effectiveScopeIds)
    return ids.filter((id) => scope.has(id))
  }, [
    annotationsWithMetricOutputs,
    effectiveFilters,
    metric,
    humanReviewProjects,
    effectiveScopeIds,
    criteriaViewMode,
    annotationProjectIds,
    isFetchingAnnotations,
  ])

  // Conversations the test actually runs on: the user's selection (or all
  // filtered when untouched), capped at MAX_TEST_CONVERSATIONS.
  const runSimIds = useMemo(
    () => resolveRunSelection(simulationIds, selectedRunIds).runIds,
    [selectedRunIds, simulationIds],
  )

  const reviewers = useMemo(() => {
    if (!annotationsWithMetricOutputs || !metric?.id) {
      return []
    }
    const annotations =
      annotationsWithMetricOutputs.annotations[metric.id] || []
    const uniqueReviewers = new Set<string>()
    for (const annotation of annotations) {
      if (annotation.assignee && !annotation.archived) {
        uniqueReviewers.add(annotation.assignee)
      }
    }
    return Array.from(uniqueReviewers).sort()
  }, [annotationsWithMetricOutputs, metric?.id])

  // Experiment runner
  const handleResult = useCallback(
    (simId: string, result: TestResult) => {
      setExperimentResults((prev) => ({
        ...prev,
        [simId]: result,
      }))
      // Composite drawer reads its per-criterion AI Review data from the
      // getSimulationOutput cache. That query isn't touched by the runner or
      // the review-metric invalidation, so after a rerun the drawer would
      // keep showing the pre-rerun output until the user navigated away.
      void invalidateSimulationOutput(simId)
    },
    [invalidateSimulationOutput],
  )

  const {
    runExperiment,
    runSingleExperiment,
    stopExperiment,
    isRunning,
    isStopping,
    evaluatingIds,
  } = useBackendExperimentRunner({
    metric,
    onResult: handleResult,
    onComplete: useCallback(() => {
      if (metric?.id) {
        invalidateReviewMetricTransport()
      }
    }, [metric?.id, invalidateReviewMetricTransport]),
    simulationIds: runSimIds, // Ordered run set (selected subset or all filtered)
    beforeRun,
    criteriaCount: criteriaOverride?.length,
  })

  useEffect(() => {
    onRunningChange?.(isRunning)
    return () => onRunningChange?.(false)
  }, [isRunning, onRunningChange])

  // Event bus listeners
  useEventBus(METRIC_VIEWER_EVENTS.TEST_METRIC_RUN, () => {
    if (!isRunning) {
      handleClearExperiment()
      void runExperiment()
    }
  })

  useEventBus(METRIC_VIEWER_EVENTS.TEST_METRIC_STOP, () => {
    stopExperiment()
  })

  // Navigation
  const currentIndex = useMemo(() => {
    if (!selectedSimulationId) {
      return -1
    }
    return simulationIds.indexOf(selectedSimulationId)
  }, [selectedSimulationId, simulationIds])

  const handleNextSimulation = useCallback(() => {
    if (currentIndex < simulationIds.length - 1) {
      handleRowClick(simulationIds[currentIndex + 1])
    }
  }, [currentIndex, simulationIds, handleRowClick])

  const handlePreviousSimulation = useCallback(() => {
    if (currentIndex > 0) {
      handleRowClick(simulationIds[currentIndex - 1])
    }
  }, [currentIndex, simulationIds, handleRowClick])

  useNavigationShortcuts({
    enabled: !!selectedSimulationId,
    includeHorizontalArrows: true,
    nextLabel: "Next simulation",
    onNext:
      currentIndex >= 0 && currentIndex < simulationIds.length - 1
        ? handleNextSimulation
        : undefined,
    onPrevious: currentIndex > 0 ? handlePreviousSimulation : undefined,
    previousLabel: "Previous simulation",
  })

  const handleRunSingleExperiment = useCallback(() => {
    if (selectedSimulationId) {
      void runSingleExperiment(selectedSimulationId)
    }
  }, [selectedSimulationId, runSingleExperiment])

  const handleClearExperiment = useCallback(() => {
    setExperimentResults({})
    clearFocusedSimulationIds()
    if (typeof window !== "undefined" && metric?.id && storageKey) {
      try {
        sessionStorage.removeItem(storageKey)
      } catch (error) {
        log.error("Failed to clear persisted experiment results", error)
      }
    }
  }, [storageKey, metric?.id, clearFocusedSimulationIds])

  // Show detail view if a simulation is selected
  if (selectedSimulationId) {
    if (metric?.supportsAudioSegmentReview) {
      return (
        <AudioMetricsSimulationDetailView
          currentIndex={currentIndex}
          totalCount={simulationIds.length}
          onNext={handleNextSimulation}
          onPrevious={handlePreviousSimulation}
          selectedProjectId={filters.project}
          experimentResult={experimentResults[selectedSimulationId] || null}
          onRunExperiment={handleRunSingleExperiment}
          isRunningExperiment={
            isRunning && evaluatingIds.has(selectedSimulationId)
          }
          bodyOverlay={bodyOverlay}
        />
      )
    }
    return (
      <SimulationDetailView
        currentIndex={currentIndex}
        totalCount={simulationIds.length}
        onNext={handleNextSimulation}
        onPrevious={handlePreviousSimulation}
        experimentResult={experimentResults[selectedSimulationId] || null}
        onRunExperiment={handleRunSingleExperiment}
        isRunningExperiment={
          isRunning && evaluatingIds.has(selectedSimulationId)
        }
        selectedProjectId={filters.project}
        bodyOverlay={bodyOverlay}
      />
    )
  }

  return (
    <>
      {/* Header */}
      <div className="px-4 py-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-3">
          <h2 className="shrink-0 text-base font-medium text-coval-text">
            {headerTitle}
          </h2>
          {headerContent}
        </div>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {canRunEvaluations && (
            <>
              {isRunning ? (
                <Button
                  variant="danger"
                  disabled={isStopping}
                  onClick={() =>
                    metricViewerEventBus.emit(
                      METRIC_VIEWER_EVENTS.TEST_METRIC_STOP,
                    )
                  }
                  aria-label="Stop experiment"
                >
                  {!isStopping && <Square className="w-3.5 h-3.5" />}
                  {isStopping ? "Stopping..." : "Stop"}
                </Button>
              ) : (
                <>
                  <span
                    className="text-xs text-coval-text-muted tabular-nums"
                    aria-label={`${runSimIds.length} of ${MAX_TEST_CONVERSATIONS} conversations selected for testing`}
                  >
                    {runSimIds.length}/{MAX_TEST_CONVERSATIONS}
                  </span>
                  <Button
                    variant="secondary"
                    disabled={runSimIds.length === 0}
                    onClick={() => {
                      trackEvent(
                        TRACKING_EVENTS.METRICS.TEST_METRIC_BUTTON_CLICKED,
                        { metric_id: metric?.id },
                      )
                      metricViewerEventBus.emit(
                        METRIC_VIEWER_EVENTS.TEST_METRIC_RUN,
                      )
                    }}
                    aria-label="Run test"
                  >
                    <Play className="w-3.5 h-3.5" />
                    {Object.keys(experimentResults).length > 0
                      ? "Re-Test"
                      : "Test"}
                  </Button>
                  {Object.keys(experimentResults).length > 0 && (
                    <Button
                      variant="danger"
                      onClick={handleClearExperiment}
                      disabled={isRunning}
                      aria-label="Clear experiment"
                    >
                      Clear Experiment
                    </Button>
                  )}
                </>
              )}
            </>
          )}
          <HumanReviewProjectFilter
            value={filters.project}
            onValueChange={(project) =>
              setFilters((previous) => ({ ...previous, project }))
            }
            projects={relevantProjects}
          />
          <AnnotationsFiltersPopover
            filters={filters}
            setFilters={setFilters}
            reviewers={reviewers}
            usesRegions={usesRegions}
            toleranceSeconds={contextTolerance}
            onToleranceSecondsChange={setContextTolerance}
            canRevealAgreement={canRevealAgreement}
          />
          <Tooltip content="Close drawer" triggerDisabled={isRunning}>
            <button
              onClick={handleClose}
              disabled={isRunning}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-surface-1 text-coval-text-muted hover:bg-surface-1 hover:text-coval-text transition-colors"
              aria-label="Close drawer"
            >
              <X className="w-4 h-4" />
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col">
        {/* Tabs */}
        {showTabs && (
          <div className="px-4 border-b border-coval-border">
            <Tabs
              value={viewMode}
              onValueChange={(value) => setViewMode(value as ViewMode)}
            >
              <TabsList>
                {tabOptions.map((tab) => (
                  <TabsTrigger
                    key={tab.key}
                    value={tab.key}
                    disabled={isRunning}
                  >
                    {tab.title}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        )}

        {scopeContent}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {metric?.supportsAudioSegmentReview ? (
            <AudioMetricsSimulationReviewTable
              filters={effectiveFilters}
              humanReviewProjects={humanReviewProjects}
              experimentResults={experimentResults}
              evaluatingIds={evaluatingIds}
            />
          ) : (
            <SimulationReviewTable
              experimentResults={experimentResults}
              evaluatingIds={evaluatingIds}
              filters={effectiveFilters}
              isRunning={isRunning}
              humanReviewProjects={humanReviewProjects}
            />
          )}
        </div>
        {bodyOverlay}
      </div>
    </>
  )
}
